import { Button, ContextAlertMark, Input, IconPlus, Loader } from '@adtech/ui';
import serverErrorsConstants from '@constants/serverErrors';
import snackbarTypes from '@constants/snackbarTypes';
import snackbarsConstants from '@constants/snackbars';
import useProjects from '@hooks/useProjects';
import useSnackbar from '@hooks/useSnackbar';
import { useAppDispatch, useAppSelector } from '@hooks/useStore';
import { userActions } from '@redux/slices/users';
import { IUsersValue } from '@typings/counter';
import formUtils from '@utils/formUtils';
import React, { useState, ChangeEvent } from 'react';
import s from './AccessUsers.pcss';
import PermissionSelect, { EAccessValues } from './Fields/PermissionSelect';

interface IProps {
    usersList: IUsersValue[];
}

const AddUserForm: React.FC<IProps> = ({ usersList }) => {
    const dispatch = useAppDispatch();

    const currentProject = useAppSelector((state) => state.projectsSlice.current);

    const [inputValue, setInputValue] = useState('');
    const [permissionValue, setPermissionValue] = useState(EAccessValues.view);
    const [emailIsValid, setEmailIsValid] = useState(false);
    const [inputError, setInputError] = useState<string>(null);
    const [serverError, setServerError] = useState<string | boolean>(null);
    const [isLoading, setIsLoading] = useState(false);

    const { setSnackbar } = useSnackbar();
    const {
        api: { editProjectUsers },
    } = useProjects();
    const getUserExists = (email) => dispatch(userActions.getUserExists(email));

    const changeInputHandler = (e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target || {};

        setEmailIsValid(formUtils.rules.required(value) && formUtils.rules.email(value));

        setInputValue(e.target.value);
        setInputError('');
        setServerError('');
    };

    const handleClick = () => {
        const ERR_MSG = 'Такой email не найден в системе, пользователю необходимо пройти регистрацию';

        const isUserExistsInList = usersList.some((user) => user.email === inputValue);

        if (isUserExistsInList) {
            setServerError('Пользователь уже добавлен');
            setEmailIsValid(false);
            return;
        }

        setIsLoading(true);

        getUserExists(inputValue)
            .then((data) => {
                if (data.payload?.exists) {
                    setServerError('');

                    const successCallback = () => {
                        setSnackbar(snackbarsConstants.ACCESS_GRANTED);
                        setInputValue('');
                    };
                    const newUsersList = [
                        ...usersList,
                        {
                            email: inputValue,
                            accessLevel: permissionValue,
                        },
                    ];
                    editProjectUsers(currentProject.id, newUsersList, successCallback);
                } else if (data.payload?.errors?.message === serverErrorsConstants.REQUEST_API_ERROR) {
                    setSnackbar('Произошла ошибка при запросе на сервер', snackbarTypes.ERROR);
                } else if (data.payload?.errors?.message === serverErrorsConstants.RATE_LIMIT_EXCEEDED) {
                    // prettier-ignore
                    // eslint-disable-next-line max-len
                    setSnackbar('В день вы можете добавить не более 20 пользователей. Чтобы дать доступ большему числу людей, вернитесь завтра или обратитесь в <a href="mailto:counter@ads.sber.ru">службу поддержки.</a>', snackbarTypes.WARNING);
                } else {
                    setServerError(ERR_MSG);
                }
            })
            .catch(() => {
                setServerError(ERR_MSG);
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <Loader loading={isLoading}>
            <div className={s.addNewUserFields}>
                <div className={s.addNewUserInputWrap}>
                    <Input
                        name="email"
                        value={inputValue}
                        placeholder="Введите email"
                        onChange={changeInputHandler}
                        status={inputError || serverError ? 'error' : ''}
                        errorText={inputError || ''}
                        autoComplete="off"
                        type="text"
                    />
                    {!serverError && (
                        <div className={s.addNewUserText}>Пользователь должен быть зарегистрирован в системе</div>
                    )}
                </div>
                <PermissionSelect
                    value={permissionValue}
                    setValue={setPermissionValue}
                    className={s.addNewUserSelect}
                />
                <Button disabled={!emailIsValid} suffixIcon={<IconPlus />} onClick={handleClick}>
                    Предоставить доступ
                </Button>
            </div>
            <div className={s.addNewUserError}>
                {serverError && <ContextAlertMark status="error" message={serverError} />}
            </div>
        </Loader>
    );
};

export default AddUserForm;
